import React, { useState, useEffect } from 'react';
import { getAgentActivity, getOperationHours } from '../services/api';
import CallDetailPage from './CallDetailPage';
import ChatDetailPage from './ChatDetailPage';
import './AgentDetailPage.css';

const AgentDetailPage = ({ agent, onClose, isRbAdmin = false, isDyopathAdmin = false, isDyopathManager = false }) => {
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // eslint-disable-line no-unused-vars
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [operationHours, setOperationHours] = useState(null);
  const [metricFilter, setMetricFilter] = useState('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');

  const fetchActivityData = async () => {
    try {
      setLoading(true);
      const params = getTimeRangeParams(timeRange);
      console.log('Fetching activity with params:', params);
      const data = await getAgentActivity(agent.id, params);
      console.log('Activity data received:', data?.count, 'records');
      setActivityData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setError('Failed to fetch activity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id, timeRange]);

  useEffect(() => {
    const loadHours = async () => {
      try {
        const data = await getOperationHours();
        setOperationHours(data.data);
      } catch (err) {
        console.error('Error fetching operation hours:', err);
      }
    };
    loadHours();
  }, []);

  const getTodayServiceHours = () => {
    if (!operationHours) return null;

    const ccaipTz = operationHours.time_zone || 'America/New_York';
    const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const now = new Date();
    const ccaipDay = new Date(now.toLocaleString('en-US', { timeZone: ccaipTz }));
    const dayKey = days[ccaipDay.getDay()];
    const startSeconds = operationHours[`${dayKey}_start_time`];
    const endSeconds = operationHours[`${dayKey}_end_time`];

    if (startSeconds === 0 && endSeconds === 0) {
      return { closed: true, dayName: now.toLocaleDateString('en-US', { weekday: 'long' }), clientTz };
    }

    const ccaipDateStr = ccaipDay.toLocaleDateString('en-CA');
    const buildTime = (secs) => {
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      return new Date(`${ccaipDateStr}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
    };
    const refDate = new Date(now.toLocaleString('en-US', { timeZone: ccaipTz }));
    const utcOffset = refDate.getTime() - now.getTime();
    const startDate = new Date(buildTime(startSeconds).getTime() - utcOffset);
    const endDate = new Date(buildTime(endSeconds).getTime() - utcOffset);

    const formatLocal = (date) => date.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: clientTz
    });

    const isOpen = now >= startDate && now < endDate;
    const opensLater = now < startDate;

    return {
      closed: false,
      dayName: now.toLocaleDateString('en-US', { weekday: 'long' }),
      start: formatLocal(startDate),
      end: formatLocal(endDate),
      clientTz,
      status: isOpen ? 'open' : opensLater ? 'not-yet' : 'closed',
      startDate,
      endDate
    };
  };

  const getTimeRangeParams = (range) => {
    const now = new Date();
    const params = {};
    
    switch (range) {
      case '24h':
        params['created_at[from]'] = new Date(now - 24 * 60 * 60 * 1000).toISOString();
        break;
      case '7d':
        params['created_at[from]'] = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '30d':
        params['created_at[from]'] = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'service-hours': {
        const today = getTodayServiceHours();
        if (today && !today.closed && today.startDate && today.endDate) {
          params['created_at[from]'] = today.startDate.toISOString();
          params['created_at[to]'] = today.endDate.toISOString();
          return params;
        }
        params['created_at[from]'] = new Date(now - 24 * 60 * 60 * 1000).toISOString();
        break;
      }
      default:
        break;
    }
    
    params['created_at[to]'] = now.toISOString();
    return params;
  };

  const calculateProductivityMetrics = () => {
    if (!activityData?.data) return null;

    const activities = activityData.data.filter(
      log => !log.activity?.startsWith('multicast')
    );
    
    // Count activities
    const activityCounts = {};
    const statusDurations = {};
    let totalCalls = 0;
    let totalChats = 0;
    let totalLoginTime = 0;

    activities.forEach(log => {
      // Count activity types
      activityCounts[log.activity] = (activityCounts[log.activity] || 0) + 1;
      
      // Sum status durations
      const statusName = log.status?.name || 'Unknown';
      statusDurations[statusName] = (statusDurations[statusName] || 0) + (log.duration || 0);
      
      // Count calls and chats
      if (log.call_id) totalCalls++;
      if (log.chat_id) totalChats++;
      
      // Sum login time
      if (log.activity === 'login' || log.activity === 'user_status_changed') {
        totalLoginTime += log.duration || 0;
      }
    });

    return {
      activityCounts,
      statusDurations,
      totalCalls,
      totalChats,
      totalLoginTime,
      avgSessionDuration: totalLoginTime / (activityCounts['login'] || 1)
    };
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActivityIcon = (activity) => {
    if (activity.includes('call')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      );
    }
    if (activity.includes('chat')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      );
    }
    if (activity === 'login') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>
      );
    }
    if (activity === 'logout') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  };

  const metrics = calculateProductivityMetrics();

  if (loading && !activityData) {
    return (
      <div className="agent-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading agent activity...</p>
        </div>
      </div>
    );
  }

  // If a call is selected, show call detail page
  if (selectedCallId) {
    return (
      <CallDetailPage
        callId={selectedCallId}
        onClose={() => setSelectedCallId(null)}
        isRbAdmin={isRbAdmin}
        isDyopathAdmin={isDyopathAdmin}
        isDyopathManager={isDyopathManager}
      />
    );
  }

  // If a chat is selected, show chat detail page
  if (selectedChatId) {
    return (
      <ChatDetailPage 
        chatId={selectedChatId} 
        onClose={() => setSelectedChatId(null)} 
      />
    );
  }

  // Get indicator color based on status (same logic as AgentCard)
  const getIndicatorColor = () => {
    if (!agent.online) return '#6b7280'; // Grey for offline
    
    const status = agent.status?.name?.toLowerCase();
    
    if (status === 'in-call' || status === 'in call') {
      return '#ef4444'; // Red for in-call
    } else if (status === 'break' || status === 'away') {
      return '#f59e0b'; // Yellow/Amber for break/away
    } else if (status === 'unavailable') {
      return '#6b7280'; // Grey for unavailable
    } else if (status === 'available') {
      return '#10b981'; // Green for available
    } else if (status === 'wrap-up' || status === 'wrapup') {
      return '#8b5cf6'; // Purple for wrap-up
    }
    
    return '#10b981'; // Default green for online
  };

  return (
    <div className="agent-detail-page">
      <div className="detail-header">
        <button className="back-btn" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        
        <div className="agent-header-info">
          <div className="agent-avatar-large">
            <img src={agent.avatar_url} alt={`${agent.first_name} ${agent.last_name}`} />
            <div 
              className="online-indicator" 
              style={{ backgroundColor: getIndicatorColor() }}
            />
          </div>
          <div className="agent-details-header">
            <h1>{agent.first_name} {agent.last_name}</h1>
            <p className="agent-email">{agent.email}</p>
            <div className="agent-meta">
              {agent.agent_number && <span className="meta-item">ID: {agent.agent_number}</span>}
              {agent.location && <span className="meta-item">📍 {agent.location}</span>}
              {agent.teams?.[0] && <span className="meta-item">👥 {agent.teams[0].name}</span>}
            </div>
          </div>
          <div className="current-status-badge" style={{
            backgroundColor: agent.status?.color === 'green' ? '#10b981' :
                           agent.status?.color === 'light_blue' ? '#3b82f6' :
                           agent.status?.color === 'purple' ? '#8b5cf6' :
                           agent.status?.color === 'yellow' ? '#f59e0b' : '#6b7280'
          }}>
            {agent.status?.name || 'Unknown'}
          </div>
        </div>
      </div>

      {/* Service Desk Hours Bar */}
      {(() => {
        const today = getTodayServiceHours();
        if (!today) return null;
        return (
          <div className={`detail-hours-bar ${today.status || (today.closed ? 'closed' : '')}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="detail-hours-icon">
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
            </svg>
            <span className="detail-hours-label">Service Desk ({today.dayName}):</span>
            {today.closed ? (
              <span className="detail-hours-value">Closed Today</span>
            ) : (
              <span className="detail-hours-value">{today.start} – {today.end}</span>
            )}
            <span className="detail-hours-tz">{today.clientTz?.replace(/_/g, ' ')}</span>
            {!today.closed && (
              <span className={`hours-status-badge ${today.status}`}>
                {today.status === 'open' ? 'Open Now' : today.status === 'not-yet' ? 'Opens Later' : 'Closed'}
              </span>
            )}
          </div>
        );
      })()}

      {/* Time Range Selector */}
      <div className="time-range-selector">
        <button
          className={timeRange === 'service-hours' ? 'active' : ''}
          onClick={() => setTimeRange('service-hours')}
        >
          Today's Service Hours
        </button>
        <button
          className={timeRange === '24h' ? 'active' : ''}
          onClick={() => setTimeRange('24h')}
        >
          Last 24 Hours
        </button>
        <button
          className={timeRange === '7d' ? 'active' : ''}
          onClick={() => setTimeRange('7d')}
        >
          Last 7 Days
        </button>
        <button
          className={timeRange === '30d' ? 'active' : ''}
          onClick={() => setTimeRange('30d')}
        >
          Last 30 Days
        </button>
      </div>
      
      {loading && <div className="loading-indicator">Loading activity data...</div>}

      {/* Productivity Metrics - Clickable to filter timeline */}
      {metrics && (
        <div className="productivity-metrics">
          <div className={`metric-card clickable-metric ${metricFilter === 'all' ? 'metric-active' : ''}`} onClick={() => setMetricFilter('all')}>
            <div className="metric-icon activities">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="metric-info">
              <div className="metric-value">{activityData?.count || 0}</div>
              <div className="metric-label">All Activities</div>
            </div>
          </div>

          <div className={`metric-card clickable-metric ${metricFilter === 'calls' ? 'metric-active' : ''}`} onClick={() => setMetricFilter(metricFilter === 'calls' ? 'all' : 'calls')}>
            <div className="metric-icon calls">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div className="metric-info">
              <div className="metric-value">{metrics.totalCalls}</div>
              <div className="metric-label">Calls</div>
            </div>
          </div>

          <div className={`metric-card clickable-metric ${metricFilter === 'chats' ? 'metric-active' : ''}`} onClick={() => setMetricFilter(metricFilter === 'chats' ? 'all' : 'chats')}>
            <div className="metric-icon chats">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div className="metric-info">
              <div className="metric-value">{metrics.totalChats}</div>
              <div className="metric-label">Chats</div>
            </div>
          </div>

          <div className={`metric-card clickable-metric ${metricFilter === 'time' ? 'metric-active' : ''}`} onClick={() => setMetricFilter(metricFilter === 'time' ? 'all' : 'time')}>
            <div className="metric-icon time">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="metric-info">
              <div className="metric-value">{formatDuration(metrics.totalLoginTime)}</div>
              <div className="metric-label">Active Time</div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Type Filter */}
      <div className="detail-filter-row">
        <div className="detail-filter-dropdown">
          <label htmlFor="activity-type-filter">Activity Type:</label>
          <select
            id="activity-type-filter"
            value={activityTypeFilter}
            onChange={(e) => setActivityTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            {metrics && Object.keys(metrics.activityCounts).sort().map(type => (
              <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        {(metricFilter !== 'all' || activityTypeFilter !== 'all') && (
          <button className="clear-filters-btn" onClick={() => { setMetricFilter('all'); setActivityTypeFilter('all'); }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Status Time Distribution */}
        {metrics && metrics.statusDurations && (
          <div className="chart-container status-time-chart">
            <h3 className="chart-heading">Time Spent by Status</h3>
            <div className="bar-chart">
              {Object.entries(metrics.statusDurations)
                .sort((a, b) => b[1] - a[1])
                .map(([status, duration]) => {
                  const maxDuration = Math.max(...Object.values(metrics.statusDurations));
                  const percentage = (duration / maxDuration) * 100;
                  
                  return (
                    <div key={status} className="bar-item">
                      <div className="bar-label-row">
                        <span className="bar-label">{status}</span>
                        <span className="bar-value">{formatDuration(duration)}</span>
                      </div>
                      <div className="bar-track">
                        <div 
                          className="bar-fill"
                          style={{ 
                            width: `${percentage}%`,
                            background: status === 'Available' ? '#10b981' :
                                      status === 'In-call' ? '#3b82f6' :
                                      status === 'Wrap-up' ? '#8b5cf6' :
                                      status === 'Away' ? '#f59e0b' : '#6b7280'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Activity Type Distribution */}
        {metrics && metrics.activityCounts && (
          <div className="chart-container activity-distribution-chart">
            <h3 className="chart-heading">Activity Breakdown</h3>
            <div className="activity-grid">
              {Object.entries(metrics.activityCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6)
                .map(([activity, count]) => (
                  <div key={activity} className="activity-item">
                    <div className="activity-icon">
                      {getActivityIcon(activity)}
                    </div>
                    <div className="activity-info">
                      <div className="activity-count">{count}</div>
                      <div className="activity-name">
                        {activity.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Activity Timeline */}
      <div className="activity-timeline-section">
        <h3 className="section-heading">Activity Timeline</h3>
        <div className="activity-table-wrapper">
          <table className="activity-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Activity</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Call ID</th>
                <th>Chat ID</th>
              </tr>
            </thead>
            <tbody>
              {activityData?.data
                ?.filter(log => !log.activity?.startsWith('multicast'))
                .filter(log => {
                  if (metricFilter === 'calls') return !!log.call_id;
                  if (metricFilter === 'chats') return !!log.chat_id;
                  if (metricFilter === 'time') return log.activity === 'login' || log.activity === 'user_status_changed';
                  return true;
                })
                .filter(log => activityTypeFilter === 'all' || log.activity === activityTypeFilter)
                .slice(0, 30).map(log => (
                <tr key={log.id} className="activity-row">
                  <td className="time-cell">{formatDateTime(log.started_at)}</td>
                  <td className="activity-cell">
                    <div className="activity-badge">
                      {getActivityIcon(log.activity)}
                      <span>{log.activity.replace(/_/g, ' ')}</span>
                    </div>
                  </td>
                  <td className="status-cell">
                    <span 
                      className="status-pill"
                      style={{
                        backgroundColor: log.status?.color === 'green' ? '#10b981' :
                                       log.status?.color === 'light_blue' ? '#3b82f6' :
                                       log.status?.color === 'purple' ? '#8b5cf6' :
                                       log.status?.color === 'yellow' ? '#f59e0b' : '#6b7280'
                      }}
                    >
                      {log.status?.name}
                    </span>
                  </td>
                  <td className="duration-cell">{formatDuration(log.duration)}</td>
                  <td className="id-cell">
                    {log.call_id ? (
                      <span 
                        className="clickable-id call-id-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCallId(log.call_id);
                        }}
                        title="Click to view call details"
                      >
                        {log.call_id}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="id-cell">
                    {log.chat_id ? (
                      <span 
                        className="clickable-id chat-id-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedChatId(log.chat_id);
                        }}
                        title="Click to view chat details"
                      >
                        {log.chat_id}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailPage;

